import React from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";

import { DownloadCSV } from "../../../templates/teacher/DownloadCSV";
import {
  Context,
  fetchSimpleTeachingMaterials,
  fetchDataDownloadCSV,
  fetchDownloadOppaCSV,
  notFoundPath,
  classDataPath,
} from "../../../teacherStore";
import {
  IQuestion,
  IOnePagePortfolioAssessmentCSVData,
  IOnePagePortfolioAssessmentCSVAssessment,
  IOnePagePortfolioAssessmentCSVIssue,
  IOnePagePortfolioAssessmentCSVQuestion,
} from "../../../state";

export const BA_WORK_SELECTION_LESSON_ID = -999;

type QuestionWithIssueId = {
  issueId: number;
  question: IOnePagePortfolioAssessmentCSVQuestion;
};

/**
 * OPPAから質問を抽出し、issueId順にソートする
 */
const extractQuestionsFromOPPA = (
  data: IOnePagePortfolioAssessmentCSVData,
): QuestionWithIssueId[] => {
  return data.teaching_material.one_page_portfolio_assessments
    .flatMap((assessment: IOnePagePortfolioAssessmentCSVAssessment) =>
      assessment.issues.flatMap((issue: IOnePagePortfolioAssessmentCSVIssue) =>
        issue.questions.map(
          (question: IOnePagePortfolioAssessmentCSVQuestion) => ({
            issueId: issue.id,
            question,
          }),
        ),
      ),
    )
    .sort((a, b) => a.issueId - b.issueId);
};

/**
 * OPPAのCSVヘッダー行を作成する
 */
const createOPPAHeaderRows = (
  data: IOnePagePortfolioAssessmentCSVData,
  questions: QuestionWithIssueId[],
): string[][] => {
  const {
    school_name,
    grade,
    class_name,
    name,
    title,
  } = data.teaching_material;

  const headerRows: string[][] = [
    ["学校名", school_name],
    ["学年", grade],
    ["クラス名", class_name],
    ["教材名", `${name} ${title}`],
  ];

  // 質問のヘッダーを追加
  questions.forEach((item, index) => {
    const questionLabel = index === 0 ? "Beforeの問い" : `Afterの問い${index}`;
    headerRows.push([questionLabel, item.question.title]);
  });

  headerRows.push(["", ""]);

  // カラムヘッダーを作成
  const columnHeaders = ["生徒名", "生徒ID"];
  questions.forEach((_, index) => {
    const answerLabel =
      index === 0 ? "Before WORK 回答" : `After WORK 回答${index}`;
    columnHeaders.push(answerLabel);
  });
  headerRows.push(columnHeaders);

  return headerRows;
};

/**
 * OPPA生徒データの行を作成する
 */
const createOPPAStudentRows = (
  data: IOnePagePortfolioAssessmentCSVData,
  questions: QuestionWithIssueId[],
): string[][] => {
  const answers = data.teaching_material.one_page_portfolio_assessment_answers;

  const studentsMap = answers.reduce((acc, answer) => {
    if (!acc[answer.student_id]) {
      acc[answer.student_id] = {
        name: answer.student_name,
        id: answer.student_id,
      };
    }
    return acc;
  }, {} as { [key: string]: { name: string; id: string } });

  const answersMap = new Map(
    answers.map(answer => [
      `${answer.student_id}-${answer.question_id}`,
      answer,
    ]),
  );

  return Object.values(studentsMap).map(student => {
    const row = [student.name, student.id];

    questions.forEach(questionItem => {
      const answerKey = `${student.id}-${questionItem.question.id}`;
      const answer = answersMap.get(answerKey);
      row.push(answer ? answer.contents : "");
    });

    return row;
  });
};

/**
 * LessonCSVデータを処理する
 */
const processLessonCSV = (lessonCSV: any): string[][] => {
  const stateDataCSV: string[][] = [];

  const listQuestions = lessonCSV.questions.filter(
    (question: IQuestion, index: number, self: any) =>
      index ===
      self.findIndex(
        (q: any) =>
          q.issue_id === question.issue_id &&
          q.issue_page === question.issue_page,
      ),
  );

  // 回答データを処理
  lessonCSV.answers.forEach((answer: any) => {
    const { statements, contents } = answer.contents?.reduce(
      (
        accumulate: { statements: string; contents: string },
        answerContent: any,
        index: number,
      ) => ({
        statements: !index
          ? answerContent.statement
          : `${accumulate.statements}/${answerContent.statement}`,
        contents: !index
          ? answerContent.content
          : `${accumulate.contents}/${answerContent.content}`,
      }),
      { statements: "", contents: "" },
    ) ?? { statements: "", contents: "" };

    const questionForAnswer = listQuestions.find(
      (question: IQuestion) => question.issue_id === answer.issue_id,
    );

    const row = [
      questionForAnswer?.issue_page,
      answer.student_name,
      answer.student_id,
      answer.section,
      statements,
      contents,
      answer.updated_at,
      answer.student_group,
      answer.like_num,
      answer.comment_num,
      answer.comment_content,
    ].map(value =>
      typeof value === "string" ? value.replaceAll('"', '""') : value,
    );

    stateDataCSV.push(row);
  });

  // タイトル行とデータを結合
  const dataTitleCSV = [
    ["学校コード", lessonCSV.school_code],
    ["クラス名", lessonCSV.class_name],
    ["学校名", `${lessonCSV.school_name_kana} ${lessonCSV.school_name}`],
    [
      "教材名",
      `${lessonCSV.teaching_material_name} ${lessonCSV.teaching_material_title}`,
    ],
    ["Lesson", lessonCSV.title],
    ["", ""],
    [
      "ページ番号",
      "生徒の名前",
      "出席番号/ID",
      "問題番号",
      "問い",
      "回答",
      "回答時刻",
      "グループ",
      "いいねの数",
      "コメントの数",
      "コメントの内容",
    ],
  ];

  return [...dataTitleCSV, ...stateDataCSV];
};

export default () => {
  const [dataCSV, setDataCSV] = React.useState<string[][]>([]);
  const [selectedLessonId, setSelectedLessonId] = React.useState<number | null>(
    null,
  );
  const [
    selectedTeachingMaterialId,
    setSelectedTeachingMaterialId,
  ] = React.useState<number | null>(null);
  const [lastFetchedMaterial, setLastFetchedMaterial] = React.useState<
    number | null
  >(null);
  const [lastFetchedLesson, setLastFetchedLesson] = React.useState<
    number | null
  >(null);
  const [
    lastProcessedLessonData,
    setLastProcessedLessonData,
  ] = React.useState<any>(null);
  const [lastProcessedOPPAData, setLastProcessedOPPAData] = React.useState<any>(
    null,
  );
  const { state, dispatch } = React.useContext(Context);
  const params = useParams<{ class_id: string }>();
  const { state: locationState } = useLocation<{ nameClass: string }>();
  const history = useHistory();

  const class_id = parseInt(params.class_id);
  const nameClass = locationState?.nameClass;

  React.useEffect(() => {
    dispatch(fetchSimpleTeachingMaterials(class_id));
  }, [class_id, dispatch]);

  React.useEffect(() => {
    if (!nameClass) {
      history.replace(notFoundPath);
    }
  }, [history, nameClass]);

  /**
   * CSVダウンロード処理
   */
  const handleDownloadCSV = React.useCallback(
    (teachingMaterialID: number, LessonID: number) => {
      if (selectedTeachingMaterialId !== teachingMaterialID) {
        setSelectedLessonId(null);
        setDataCSV([]);
      }

      setSelectedLessonId(LessonID);
      setSelectedTeachingMaterialId(teachingMaterialID);
      setDataCSV([]);

      if (LessonID === BA_WORK_SELECTION_LESSON_ID) {
        dispatch(fetchDownloadOppaCSV(teachingMaterialID, class_id));
      } else {
        dispatch(fetchDataDownloadCSV(LessonID, class_id, teachingMaterialID));
      }

      setLastFetchedMaterial(teachingMaterialID);
      setLastFetchedLesson(LessonID);
    },
    [selectedTeachingMaterialId, dispatch, class_id],
  );

  // OPPAデータ処理
  React.useEffect(() => {
    const shouldProcessOPPA =
      selectedLessonId === BA_WORK_SELECTION_LESSON_ID &&
      state.onePagePortfolioAssessmentCSV &&
      lastFetchedMaterial === selectedTeachingMaterialId &&
      lastFetchedLesson === selectedLessonId &&
      state.onePagePortfolioAssessmentCSV.teaching_material.id ===
        selectedTeachingMaterialId &&
      state.onePagePortfolioAssessmentCSV !== lastProcessedOPPAData;

    if (shouldProcessOPPA && state.onePagePortfolioAssessmentCSV) {
      const questions = extractQuestionsFromOPPA(
        state.onePagePortfolioAssessmentCSV,
      );
      const headerRows = createOPPAHeaderRows(
        state.onePagePortfolioAssessmentCSV,
        questions,
      );
      const studentRows = createOPPAStudentRows(
        state.onePagePortfolioAssessmentCSV,
        questions,
      );

      setDataCSV([...headerRows, ...studentRows]);
      setLastProcessedOPPAData(state.onePagePortfolioAssessmentCSV);
    }
  }, [
    state.onePagePortfolioAssessmentCSV,
    selectedLessonId,
    selectedTeachingMaterialId,
    lastFetchedMaterial,
    lastFetchedLesson,
    lastProcessedOPPAData,
  ]);

  // レッスンデータ処理
  React.useEffect(() => {
    const shouldProcessLesson =
      selectedLessonId !== BA_WORK_SELECTION_LESSON_ID &&
      state.lessonCSV &&
      lastFetchedMaterial === selectedTeachingMaterialId &&
      lastFetchedLesson === selectedLessonId &&
      state.lessonCSV !== lastProcessedLessonData;

    if (shouldProcessLesson) {
      const csvData = processLessonCSV(state.lessonCSV);
      setDataCSV(csvData);
      setLastProcessedLessonData(state.lessonCSV);
    }
  }, [
    state.lessonCSV,
    selectedLessonId,
    selectedTeachingMaterialId,
    lastFetchedMaterial,
    lastFetchedLesson,
    lastProcessedLessonData,
  ]);

  const links = React.useMemo(
    () => [
      { name: "クラスデータ", to: classDataPath },
      {
        name: nameClass ?? "",
        to: `${classDataPath}/${class_id}`,
        state: { nameClass },
      },
      { name: "回答データのダウンロード", to: "#" },
    ],
    [nameClass, class_id],
  );

  const goBack = React.useCallback(
    () => history.push(`${classDataPath}/${class_id}`, { nameClass }),
    [class_id, history, nameClass],
  );

  if (!nameClass) {
    return null;
  }

  return (
    <DownloadCSV
      links={links}
      teaching_materials={state.simple_teaching_materials}
      nameClass={nameClass}
      onDownLoadData={handleDownloadCSV}
      goBack={goBack}
      dataCSV={dataCSV}
    />
  );
};
